/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback } from 'react';

import { Button, makeStyles, Typography } from '@material-ui/core';
import { Add, Delete, DragHandle } from '@material-ui/icons';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import { DCM, DOCS } from '../../models/Resource';

const useStyle = makeStyles((theme) => {
  return {
    deleteIcon: {
      color: theme.palette.grey['500'],
      cursor: 'pointer',
      fontSize: '20px',
    },

    imageContainer: {
      alignItems: 'center',
      background: '#172B4D',
      borderRadius: '8px',
      color: '#2AAAE2',
      display: 'flex',
      height: '52px',
      justifyContent: 'center',
      marginRight: '8px',
      width: '64px',
    },

    imageContainerFilled: {
      backgroundPosition: 'center',
      backgroundSize: 'cover',
    },

    imageTypography: {
      color: '#2AAAE2',
      fontSize: '14px',
      fontWeight: 'bold',
      lineHeight: 1,
      marginBottom: 0,
    },

    materialAddButton: {
      marginTop: '8px',
    },

    materialItemDescription: {
      color: theme.palette.primary.main,
      fontWeight: 400,
      lineHeight: '120%',
      margin: '0 4px 0 0',
    },

    materialItemHandle: {
      color: theme.palette.primary.main,
      cursor: 'grab',
      fontWeight: 500,
      marginRight: '8px',
    },

    materialItemTitle: {
      color: theme.palette.primary.main,
      fontWeight: 600,
      lineHeight: '120%',
      margin: '0 4px 0 0',
    },

    materialItemsContainer: {
      alignItems: 'center',
      background: '#fff',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '12px',
      display: 'flex',
      flexWrap: 'nowrap',
      marginTop: '8px',
      padding: '8px',
    },

    materialTitle: {
      color: '#172b4d',
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: 0,
      marginRight: '8px',
      marginTop: '8px',
    },

    materialTitleContainer: {
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      marginBottom: '8px',
    },

    textNoMaterialContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    },

    textNoMaterialSelected: {
      color: theme.palette.grey['500'],
      fontSize: '14px',
      marginBottom: '16px',
      marginTop: '8px',
      maxWidth: '300px',
      textAlign: 'center',
    },
  };
});

const SortHandle = SortableHandle(({ classes }) => <DragHandle className={classes.materialItemHandle} />);

const SortableItem = SortableElement(({ classes, onOpenViewer, m, onRemove, isAllowedToEdit }) => {
  return (
    <div className={classes.materialItemsContainer}>
      {isAllowedToEdit ? <SortHandle classes={classes} /> : null}

      <div onClick={() => onOpenViewer(m.material)} style={{ cursor: 'pointer' }}>
        <RenderMaterialThumbnail m={m} classes={classes} />
      </div>

      <div onClick={() => onOpenViewer(m.material)} style={{ cursor: 'pointer' }}>
        <p className={classes.materialItemTitle}>{m.material?.title || 'No title'}</p>
        {m?.material?.description ? <p className={classes.materialItemDescription}>{m.material?.description}</p> : null}
      </div>

      <div style={{ flexGrow: 1 }} />

      {isAllowedToEdit ? <Delete className={classes.deleteIcon} onClick={() => onRemove(m.id)} /> : null}
    </div>
  );
});

const SortableList = SortableContainer(({ items, keyTemplate, classes, onOpenViewer, onRemove, isAllowedToEdit }) => {
  return (
    <ul style={{ padding: 0 }}>
      {items.map((value, index) => (
        <SortableItem
          key={`${keyTemplate || 'random'}-item-${value?.materialId}`}
          index={index}
          value={value}
          classes={classes}
          onOpenViewer={onOpenViewer}
          m={value}
          onRemove={onRemove}
          isAllowedToEdit={isAllowedToEdit}
        />
      ))}
    </ul>
  );
});

const RenderMaterialThumbnail = ({ m, classes }) => {
  const material = m?.material || {};

  if (material?.resource?.type === DCM) {
    return (
      <div className={classes.imageContainer}>
        <Typography className={classes.imageTypography}>DCM</Typography>
      </div>
    );
  }

  if (material?.resource?.type === DOCS) {
    return (
      <div className={classes.imageContainer}>
        <Typography className={classes.imageTypography}>DOCS</Typography>
      </div>
    );
  }

  return <div className={`${classes.imageContainer} ${classes.imageContainerFilled}`} style={{ backgroundImage: `url(${material?.resource?.thumbnailPath})` }} />;
};

export default ({
  template,
  onAddEducational,
  onAddSupplementary,
  onSortEducational,
  onSortSupplementary,
  onRemoveEducationById,
  onOpenViewer,
  onRemoveSupplementaryById,
  isAllowedToEdit,
}) => {
  const classes = useStyle();

  const handleAddSupplementaryClick = useCallback(
    (supplementaryMaterials) => {
      if (onAddSupplementary) {
        const supplementary = (supplementaryMaterials || []).map((e) => {
          return {
            ...e,
            id: e.materialId || e.id,
          };
        });

        onAddSupplementary({ selectedMaterials: supplementary });
      }
    },
    [onAddSupplementary]
  );

  const handleAddEducationalClick = useCallback(
    (educationalMaterials) => {
      if (onAddEducational) {
        const educationals = (educationalMaterials || []).map((e) => {
          return {
            ...e,
            id: e.materialId || e.id,
          };
        });

        onAddEducational({ selectedMaterials: educationals });
      }
    },
    [onAddEducational]
  );

  return (
    <div>
      {/* EDUCATIONAL */}
      <div className={classes.materialTitleContainer}>
        <Typography className={classes.materialTitle}>Educational materials</Typography>
        {isAllowedToEdit ? (
          <Button color="primary" variant="contained" onClick={() => handleAddEducationalClick(template?.educationalMaterials || [])} className={classes.materialAddButton}>
            <Add style={{ marginRight: '4px' }} /> Add
          </Button>
        ) : null}
      </div>

      <div>
        {template?.educationalMaterials?.length ? (
          <SortableList
            useDragHandle
            items={template?.educationalMaterials || []}
            classes={classes}
            keyTemplate="educational"
            onOpenViewer={onOpenViewer}
            onRemove={onRemoveEducationById}
            isAllowedToEdit={isAllowedToEdit}
            onSortEnd={onSortEducational}
            axis="y"
          />
        ) : (
          <div className={classes.textNoMaterialContainer}>
            <Typography className={classes.textNoMaterialSelected}>No material selected. Please add the materials by clicking the add button above.</Typography>
          </div>
        )}
      </div>

      {/* SUPPLEMENTARY */}
      <div className={classes.materialTitleContainer} style={{ marginTop: '20px' }}>
        <Typography className={classes.materialTitle}>Supplementary materials</Typography>
        {isAllowedToEdit ? (
          <Button color="primary" variant="contained" onClick={() => handleAddSupplementaryClick(template?.supplementaryMaterials || [])} className={classes.materialAddButton}>
            <Add style={{ marginRight: '4px' }} /> Add
          </Button>
        ) : null}
      </div>

      <div>
        {template?.supplementaryMaterials?.length ? (
          <SortableList
            useDragHandle
            items={template?.supplementaryMaterials || []}
            classes={classes}
            keyTemplate="supplementary"
            onOpenViewer={onOpenViewer}
            onRemove={onRemoveSupplementaryById}
            isAllowedToEdit={isAllowedToEdit}
            onSortEnd={onSortSupplementary}
            axis="y"
          />
        ) : (
          <div className={classes.textNoMaterialContainer}>
            <Typography className={classes.textNoMaterialSelected}>No material selected. Please add the materials by clicking the add button above.</Typography>
          </div>
        )}
      </div>
    </div>
  );
};
